import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarIcon, TagIcon } from '@heroicons/react/24/outline';
import apiService from '../services/api';

interface NewsItem {
  _id: string;
  title: string;
  category: string;
  summary: string;
  content: string;
  author: string;
  tags: string[];
  isPublished: boolean;
  publishDate?: string;
  viewCount: number;
  createdAt: string;
  updatedAt: string;
  coverImage?: string; // Added coverImage to the interface
}

const News: React.FC = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('全部');
  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);

  const categories = ['全部', '学术会议', '合作交流', '实验室活动', '获奖消息', '招生/招聘信息', '其他'];

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line
  }, [selectedCategory]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const params = selectedCategory === '全部' ? {} : { category: selectedCategory };
      console.log('获取新闻，分类:', selectedCategory, '参数:', params);
      const response = await apiService.get<any>('/news', params);
      console.log('新闻API响应:', response);
      console.log('新闻数据:', response.data);
      console.log('新闻数量:', response.data?.length || 0);
      setNews(response.data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* 页面标题 */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">新闻动态</h1>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          了解我们课题组的最新研究成果、学术活动、招生信息等动态。
        </p>
      </div>

      {/* 分类筛选 */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>
      </div>

      {/* 新闻列表 */}
      <div className="space-y-8">
        {loading ? (
          <div className="text-center text-gray-400">加载中...</div>
        ) : news.length === 0 ? (
          <div className="text-center text-gray-400">暂无新闻</div>
        ) : (
          news.map((item) => (
            <article key={item._id} className="card">
              <div className="flex items-start space-x-6">
                {/* 封面图片 */}
                {item.coverImage && (
                  <div className="flex-shrink-0">
                    <img
                      src={item.coverImage}
                      alt={item.title}
                      className="w-32 h-24 object-cover rounded-lg"
                    />
                  </div>
                )}
                
                {/* 新闻内容 */}
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <span className="inline-block bg-primary-100 text-primary-800 text-xs px-2 py-1 rounded">
                        {item.category}
                      </span>
                      <div className="flex items-center text-sm text-gray-500">
                        <CalendarIcon className="h-4 w-4 mr-1" />
                        {new Date(item.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h2>
                  <p className="text-gray-600 mb-4 leading-relaxed">{item.summary}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <span className="text-sm text-gray-500">作者：{item.author}</span>
                      <div className="flex items-center space-x-2">
                        <TagIcon className="h-4 w-4 text-gray-400" />
                        <div className="flex space-x-1">
                          {item.tags?.map((tag) => (
                            <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* 查看详情按钮 */}
                  <div className="mt-4">
                    <button
                      onClick={() => navigate(`/news/${item._id}`)}
                      className="inline-flex items-center px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                      查看详情
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
};

export default News; 